import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import type { Entity } from "@/lib/api";
import { cn } from "@/lib/cn";

/**
 * The sortable columns the entity tables share (Entities, Initiatives): name, mentions and
 * "last heard". Sorting happens in the browser, over the list the app already holds.
 */

export type SortKey = "name" | "mentions" | "last";
export type Sort = { key: SortKey; dir: "asc" | "desc" };

/** `entities` sorted by the chosen column — most mentioned first until somebody picks one. */
export function useEntitySort(entities: Entity[]) {
  const [sort, setSort] = useState<Sort>({ key: "mentions", dir: "desc" });

  const sorted = useMemo(
    () => [...entities].sort((a, b) => compare(a, b, sort.key) * (sort.dir === "asc" ? 1 : -1)),
    [entities, sort],
  );

  function toggleSort(key: SortKey) {
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "name" ? "asc" : "desc" },
    );
  }

  return { sorted, sort, toggleSort };
}

export function SortHeader({
  label,
  me,
  active,
  onClick,
}: {
  label: string;
  me: SortKey;
  active: Sort;
  onClick: (key: SortKey) => void;
}) {
  const on = active.key === me;
  const Icon = !on ? ChevronsUpDown : active.dir === "asc" ? ArrowUp : ArrowDown;

  return (
    <button
      type="button"
      onClick={() => onClick(me)}
      className={cn(
        "flex w-fit items-center gap-1.5 bg-transparent p-0 text-left text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wide)] transition-colors hover:text-[var(--text-primary)]",
        on ? "text-[var(--text-secondary)]" : "text-[var(--text-muted)]",
      )}
      aria-label={`Sort by ${label.toLowerCase()}`}
    >
      {label}
      <Icon size={12} strokeWidth={2} className={cn(!on && "opacity-40")} />
    </button>
  );
}

function compare(a: Entity, b: Entity, key: SortKey): number {
  if (key === "name") return a.name.localeCompare(b.name);
  if (key === "mentions") return a.mentionCount - b.mentionCount || a.name.localeCompare(b.name);

  // "last heard" — an entity nobody has said yet sorts below every one that has.
  const at = a.lastMentionedAt ? Date.parse(a.lastMentionedAt) : -Infinity;
  const bt = b.lastMentionedAt ? Date.parse(b.lastMentionedAt) : -Infinity;
  return at - bt || a.name.localeCompare(b.name);
}
